import { useTranslation } from 'react-i18next';

import { isApiError, ERROR_CODES } from '@/lib/apiProblem';
import type { ApiProblem } from '@/types/api';

/**
 * Maps a stable problem code onto a translation key.
 *
 * The API answers with a localized `title`/`detail` already, but the SPA prefers its own copy: the wording stays
 * under product control, and a code the server adds tomorrow degrades to the generic sentence instead of showing
 * a machine string.
 */
export const PROBLEM_MESSAGE_KEYS: Readonly<Record<string, string>> = {
  [ERROR_CODES.validationError]: 'errors.validation_error',
  [ERROR_CODES.notFound]: 'errors.not_found',
  [ERROR_CODES.duplicateResource]: 'errors.duplicate_resource',
  [ERROR_CODES.concurrencyConflict]: 'errors.concurrency_conflict',
  [ERROR_CODES.forbidden]: 'errors.forbidden',
  [ERROR_CODES.invalidCredentials]: 'errors.invalid_credentials',
  [ERROR_CODES.unauthenticated]: 'errors.unauthenticated',
  [ERROR_CODES.tokenExpired]: 'errors.unauthenticated',
  [ERROR_CODES.refreshTokenInvalid]: 'errors.unauthenticated',
  [ERROR_CODES.accountLocked]: 'errors.account_locked',
  [ERROR_CODES.accountBanned]: 'errors.account_banned',
  [ERROR_CODES.securityTokenInvalid]: 'errors.security_token_invalid',
  [ERROR_CODES.emailNotVerified]: 'errors.email_not_verified',
  [ERROR_CODES.localLoginDisabled]: 'errors.local_login_disabled',
  [ERROR_CODES.externalLoginUnavailable]: 'errors.external_login_unavailable',
  [ERROR_CODES.organisationTokenInvalid]: 'errors.organisation_token_invalid',
  [ERROR_CODES.organisationUsernameMissing]: 'errors.organisation_username_missing',
  [ERROR_CODES.organisationUsernameTaken]: 'errors.organisation_username_taken',
  [ERROR_CODES.organisationProviderUnavailable]: 'errors.organisation_provider_unavailable',
  [ERROR_CODES.domainRuleViolation]: 'errors.domain_rule_violation',
  [ERROR_CODES.promptQualityInsufficient]: 'errors.prompt_quality_insufficient',
  [ERROR_CODES.idempotencyConflict]: 'errors.idempotency_conflict',
  [ERROR_CODES.quotaExceeded]: 'errors.quota_exceeded',
  [ERROR_CODES.sharePasswordRequired]: 'errors.share_password_required',
  [ERROR_CODES.versionBodyNotRetained]: 'errors.version_body_not_retained',
  [ERROR_CODES.adminUserRoleUnchanged]: 'errors.admin_user_role_unchanged',
  [ERROR_CODES.rateLimited]: 'errors.rate_limited',
  [ERROR_CODES.payloadTooLarge]: 'errors.payload_too_large',
  [ERROR_CODES.internalError]: 'errors.internal_error',
  [ERROR_CODES.aiProviderError]: 'errors.ai_provider_error',
  [ERROR_CODES.aiProviderTimeout]: 'errors.ai_provider_timeout',
  [ERROR_CODES.aiModelMissing]: 'errors.ai_model_missing',
  [ERROR_CODES.aiResponseTruncated]: 'errors.ai_response_truncated',
  [ERROR_CODES.generationSourceEmpty]: 'errors.generation_source_empty',
  [ERROR_CODES.generationSourceAmbiguous]: 'errors.generation_source_ambiguous',
  [ERROR_CODES.generationSourceUnsupported]: 'errors.generation_source_unsupported',
  [ERROR_CODES.generationSourceTooLarge]: 'errors.generation_source_too_large',
  [ERROR_CODES.generationResponseMalformed]: 'errors.generation_response_malformed',
  [ERROR_CODES.generationStale]: 'errors.generation_stale',
  [ERROR_CODES.planUpgradeRequired]: 'errors.plan_upgrade_required',
  [ERROR_CODES.serviceUnavailable]: 'errors.service_unavailable',
  [ERROR_CODES.networkError]: 'errors.network_error',
  [ERROR_CODES.unexpectedError]: 'errors.unexpected_error',
};

/** Resolves the translation key of a problem document, always to a key that exists. */
export function problemMessageKey(problem: ApiProblem): string {
  return PROBLEM_MESSAGE_KEYS[problem.code] ?? 'errors.generic';
}

/**
 * Renders a user-facing message for any thrown value.
 *
 * Field-level validation problems are summarized rather than itemized here; the individual sentences are shown
 * next to their inputs by `applyServerFieldErrors`.
 */
export function useProblemMessage(): (error: unknown) => string {
  const { t } = useTranslation();

  return (error: unknown) => {
    if (!isApiError(error)) {
      return t('errors.unexpected_error');
    }

    const key = problemMessageKey(error.problem);

    // A rate-limited answer is the one case where the server's own detail ("try again in N seconds") is more
    // useful than a generic sentence, so the countdown is interpolated into the localized copy.
    const retryAfter = error.problem.retryAfterSeconds;

    if (key === 'errors.rate_limited' && typeof retryAfter === 'number') {
      return t('errors.rate_limited_retry', { seconds: retryAfter });
    }

    return t(key);
  };
}
